/**
 * Unified worker pool that manages reusable worker instances.
 *
 * Merges the functionality of the old WorkerPool (simple get/return) and
 * WorkerManager (max concurrency, idle cleanup, active tracking).
 *
 * Features:
 * - Pooling by key (typically the worker URL)
 * - Configurable max concurrent workers and idle timeout
 * - Supports both module and classic workers
 * - Clears event handlers on return to prevent memory leaks
 * - Proper queue when at max capacity (no polling)
 */

const DEFAULT_MAX_WORKERS = 20;
const DEFAULT_MAX_IDLE_TIME_MS = 5000;

export interface WorkerEntry {
  worker: Worker;
  lastUsed: number;
  id: number;
}

interface WorkerPoolOptions {
  maxWorkers?: number;
  maxIdleTimeMs?: number;
}

/** Queued request waiting for a worker to become available */
interface QueuedRequest {
  key: string;
  workerOptions?: WorkerOptions;
  resolve: (entry: WorkerEntry) => void;
}

let nextWorkerId = 0;

export class UnifiedWorkerPool {
  private idleWorkers: Record<string, WorkerEntry[]> = {};
  private activeWorkers = new Set<Worker>();
  private activeCount = 0;
  private maxWorkers: number;
  private maxIdleTimeMs: number;
  private cleanupIntervalId: ReturnType<typeof setInterval> | null = null;
  private waitQueue: QueuedRequest[] = [];

  constructor(options?: WorkerPoolOptions) {
    this.maxWorkers = options?.maxWorkers ?? DEFAULT_MAX_WORKERS;
    this.maxIdleTimeMs = options?.maxIdleTimeMs ?? DEFAULT_MAX_IDLE_TIME_MS;
    this.startIdleCleanup();
  }

  /**
   * Get a worker from the pool, creating a new one if needed.
   * If at max capacity, the returned promise waits until a worker is returned.
   *
   * @param key Identifier for the worker type (typically the URL)
   * @param workerOptions Options for creating new workers (e.g. { type: 'module' })
   */
  getWorker(key: string, workerOptions?: WorkerOptions): Promise<WorkerEntry> {
    // Try to reuse an idle worker for this key
    const idle = this.idleWorkers[key]?.pop();
    if (idle) {
      this.activeCount++;
      this.activeWorkers.add(idle.worker);
      idle.lastUsed = Date.now();
      return Promise.resolve(idle);
    }

    // If under capacity, create a new worker
    if (this.activeCount < this.maxWorkers) {
      this.activeCount++;
      const entry: WorkerEntry = {
        worker: new Worker(key, workerOptions),
        lastUsed: Date.now(),
        id: nextWorkerId++,
      };
      this.activeWorkers.add(entry.worker);
      return Promise.resolve(entry);
    }

    // At capacity — queue the request
    return new Promise<WorkerEntry>((resolve) => {
      this.waitQueue.push({ key, workerOptions, resolve });
    });
  }

  /**
   * Return a worker to the pool for reuse.
   * Clears event handlers to prevent memory leaks.
   */
  returnWorker(key: string, entry: WorkerEntry): void {
    this.activeCount--;
    this.activeWorkers.delete(entry.worker);

    // Clear handlers
    entry.worker.onmessage = null;
    entry.worker.onerror = null;
    entry.lastUsed = Date.now();

    // If there are queued requests, fulfill the next one instead of pooling
    if (this.waitQueue.length > 0) {
      const queued = this.waitQueue.shift();
      if (!queued) return;
      if (queued.key === key) {
        // Same key — reuse this worker directly
        this.activeCount++;
        this.activeWorkers.add(entry.worker);
        queued.resolve(entry);
      } else {
        // Different key — pool this worker and hand the freed slot to the queued request
        if (!this.idleWorkers[key]) this.idleWorkers[key] = [];
        this.idleWorkers[key].push(entry);
        this.getWorker(queued.key, queued.workerOptions).then(queued.resolve);
      }
      return;
    }

    // No queued requests — return to idle pool
    if (!this.idleWorkers[key]) this.idleWorkers[key] = [];
    this.idleWorkers[key].push(entry);
  }

  /**
   * Terminate a worker that errored instead of pooling it. A worker whose
   * script failed to load (e.g. importScripts network error) is dead and
   * would never answer a later postMessage.
   */
  discardWorker(entry: WorkerEntry): void {
    this.activeCount--;
    this.activeWorkers.delete(entry.worker);
    entry.worker.onmessage = null;
    entry.worker.onerror = null;
    entry.worker.terminate();

    // Freed a slot, so this won't queue again
    const queued = this.waitQueue.shift();
    if (queued) this.getWorker(queued.key, queued.workerOptions).then(queued.resolve);
  }

  /**
   * Terminate all workers (idle + active) and reset state.
   * Call this on viewer destroy / page navigation.
   */
  cleanup(): void {
    // Stop idle cleanup
    if (this.cleanupIntervalId !== null) {
      clearInterval(this.cleanupIntervalId);
      this.cleanupIntervalId = null;
    }

    // Terminate idle workers
    for (const key of Object.keys(this.idleWorkers)) {
      for (const entry of this.idleWorkers[key]) {
        entry.worker.terminate();
      }
    }
    this.idleWorkers = {};

    // Terminate active workers
    this.activeWorkers.forEach((worker) => {
      worker.onmessage = null;
      worker.onerror = null;
      worker.terminate();
    });
    this.activeWorkers.clear();
    this.activeCount = 0;

    // Reject queued requests
    this.waitQueue = [];

    // Restart cleanup interval for the next viewer instance
    this.startIdleCleanup();
  }

  private startIdleCleanup(): void {
    if (this.cleanupIntervalId !== null) {
      clearInterval(this.cleanupIntervalId);
    }
    this.cleanupIntervalId = setInterval(() => {
      const now = Date.now();
      for (const key of Object.keys(this.idleWorkers)) {
        this.idleWorkers[key] = this.idleWorkers[key].filter((entry) => {
          const shouldRemove = now - entry.lastUsed > this.maxIdleTimeMs;
          if (shouldRemove) entry.worker.terminate();
          return !shouldRemove;
        });
      }
    }, this.maxIdleTimeMs);
  }
}

/** Singleton instance — replaces both the old WorkerPool and WorkerManager */
const unifiedWorkerPool = new UnifiedWorkerPool();

export default unifiedWorkerPool;
